/**
 * Sky — gradient backdrop with day/night cycle support.
 *
 * Listens to the festival clock and shifts colors smoothly through dawn,
 * day, golden-hour, dusk, and night.
 */

import * as THREE from 'three';
import type { FestivalTime } from '@shared/festival-clock';

interface SkyPalette {
  top: THREE.Color;
  mid: THREE.Color;
  bottom: THREE.Color;
  sun: THREE.Color;
  ambient: THREE.Color;
  fog: THREE.Color;
  exposure: number;
}

const PALETTES: { hour: number; p: SkyPalette }[] = [
  {
    hour: 0,
    p: {
      top: new THREE.Color('#04030c'),
      mid: new THREE.Color('#0a0820'),
      bottom: new THREE.Color('#1a1430'),
      sun: new THREE.Color('#1a1a4a'),
      ambient: new THREE.Color('#1a1830'),
      fog: new THREE.Color('#0a0820'),
      exposure: 0.6
    }
  },
  {
    hour: 5,
    p: {
      top: new THREE.Color('#0a1430'),
      mid: new THREE.Color('#3a2440'),
      bottom: new THREE.Color('#a04050'),
      sun: new THREE.Color('#ff8a5a'),
      ambient: new THREE.Color('#5a3838'),
      fog: new THREE.Color('#a86068'),
      exposure: 0.85
    }
  },
  {
    hour: 8,
    p: {
      top: new THREE.Color('#1f54c4'),
      mid: new THREE.Color('#76b6ee'),
      bottom: new THREE.Color('#ffe3c0'),
      sun: new THREE.Color('#fff0d4'),
      ambient: new THREE.Color('#7c92a8'),
      fog: new THREE.Color('#bfd0e0'),
      exposure: 1.25
    }
  },
  {
    hour: 13,
    p: {
      top: new THREE.Color('#1d5fcf'),
      mid: new THREE.Color('#7fb9ec'),
      bottom: new THREE.Color('#ffe6b8'),
      sun: new THREE.Color('#ffffff'),
      ambient: new THREE.Color('#7d9aac'),
      fog: new THREE.Color('#bcd0e0'),
      exposure: 1.35
    }
  },
  {
    hour: 17,
    p: {
      top: new THREE.Color('#3454c0'),
      mid: new THREE.Color('#ff8a4a'),
      bottom: new THREE.Color('#ffd28a'),
      sun: new THREE.Color('#ffb060'),
      ambient: new THREE.Color('#a86850'),
      fog: new THREE.Color('#e8a060'),
      exposure: 1.4
    }
  },
  {
    hour: 19,
    p: {
      top: new THREE.Color('#1c1846'),
      mid: new THREE.Color('#ff5e3a'),
      bottom: new THREE.Color('#ffa050'),
      sun: new THREE.Color('#ff7a30'),
      ambient: new THREE.Color('#7a4438'),
      fog: new THREE.Color('#cf6438'),
      exposure: 1.2
    }
  },
  {
    hour: 21,
    p: {
      top: new THREE.Color('#08081a'),
      mid: new THREE.Color('#1a1638'),
      bottom: new THREE.Color('#3a2854'),
      sun: new THREE.Color('#604080'),
      ambient: new THREE.Color('#2a2440'),
      fog: new THREE.Color('#1a1838'),
      exposure: 0.75
    }
  }
];

function lerpColor(a: THREE.Color, b: THREE.Color, t: number, out: THREE.Color): void {
  out.r = a.r + (b.r - a.r) * t;
  out.g = a.g + (b.g - a.g) * t;
  out.b = a.b + (b.b - a.b) * t;
}

export class Sky {
  readonly mesh: THREE.Mesh;
  readonly hemi: THREE.HemisphereLight;
  readonly sun: THREE.DirectionalLight;
  readonly fog: THREE.Fog;
  readonly material: THREE.ShaderMaterial;

  private working = {
    top: new THREE.Color(),
    mid: new THREE.Color(),
    bottom: new THREE.Color(),
    sun: new THREE.Color(),
    ambient: new THREE.Color(),
    fog: new THREE.Color()
  };

  exposureTarget = 1.0;

  constructor() {
    const geo = new THREE.SphereGeometry(2400, 32, 16);
    this.material = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        topColor: { value: new THREE.Color() },
        midColor: { value: new THREE.Color() },
        bottomColor: { value: new THREE.Color() }
      },
      vertexShader: /* glsl */ `
        varying vec3 vWorld;
        void main() {
          vWorld = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 topColor;
        uniform vec3 midColor;
        uniform vec3 bottomColor;
        varying vec3 vWorld;
        void main() {
          float h = clamp(vWorld.y * 0.5 + 0.5, 0.0, 1.0);
          vec3 col = mix(bottomColor, midColor, smoothstep(0.0, 0.55, h));
          col = mix(col, topColor, smoothstep(0.45, 1.0, h));
          gl_FragColor = vec4(col, 1.0);
        }
      `
    });
    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.name = 'Sky';

    this.hemi = new THREE.HemisphereLight('#ffe5b8', '#3a2a44', 1.1);
    this.sun = new THREE.DirectionalLight('#ffc371', 2.6);
    this.sun.position.set(60, 120, 40);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 800;
    const d = 220;
    this.sun.shadow.camera.left = -d;
    this.sun.shadow.camera.right = d;
    this.sun.shadow.camera.top = d;
    this.sun.shadow.camera.bottom = -d;
    this.sun.shadow.bias = -0.0005;

    // Push fog far out so distant stages remain visible. Sky is at 2400.
    this.fog = new THREE.Fog('#cdd6df', 400, 2200);
  }

  update(time: FestivalTime): void {
    const hourFloat = time.hour + time.minute / 60;
    let prev = PALETTES[PALETTES.length - 1];
    let next = PALETTES[0];
    for (let i = 0; i < PALETTES.length; i++) {
      if (PALETTES[i].hour <= hourFloat) prev = PALETTES[i];
    }
    for (let i = 0; i < PALETTES.length; i++) {
      if (PALETTES[i].hour > hourFloat) {
        next = PALETTES[i];
        break;
      }
    }
    if (next === prev) {
      next = PALETTES[(PALETTES.indexOf(prev) + 1) % PALETTES.length];
    }
    const span = (next.hour > prev.hour ? next.hour : next.hour + 24) - prev.hour;
    const into = hourFloat >= prev.hour ? hourFloat - prev.hour : hourFloat + 24 - prev.hour;
    const t = Math.min(1, Math.max(0, into / span));

    const w = this.working;
    lerpColor(prev.p.top, next.p.top, t, w.top);
    lerpColor(prev.p.mid, next.p.mid, t, w.mid);
    lerpColor(prev.p.bottom, next.p.bottom, t, w.bottom);
    lerpColor(prev.p.sun, next.p.sun, t, w.sun);
    lerpColor(prev.p.ambient, next.p.ambient, t, w.ambient);
    lerpColor(prev.p.fog, next.p.fog, t, w.fog);

    (this.material.uniforms.topColor.value as THREE.Color).copy(w.top);
    (this.material.uniforms.midColor.value as THREE.Color).copy(w.mid);
    (this.material.uniforms.bottomColor.value as THREE.Color).copy(w.bottom);

    this.sun.color.copy(w.sun);
    this.hemi.color.copy(w.mid);
    this.hemi.groundColor.copy(w.ambient);
    this.fog.color.copy(w.fog);

    // Sun rises in the east, sets in the west.
    const angle = (hourFloat / 24) * Math.PI * 2 - Math.PI / 2;
    this.sun.position.set(Math.cos(angle) * 200, Math.max(40, Math.sin(angle) * 240), 60);
    this.sun.intensity = Math.max(0.25, Math.sin(angle)) * 2.4 + 0.4;
    this.hemi.intensity = Math.max(0.4, Math.sin(angle)) * 1.0 + 0.5;

    this.exposureTarget =
      prev.p.exposure + (next.p.exposure - prev.p.exposure) * t;
  }
}
